import React, { useState } from "react";
import { FaPlus } from "react-icons/fa";
import { TeamScoreCard } from "./TeamScoreCard/index";
import { SinglePageTeamCreation } from "../../createTeam";
import { SecondaryButton } from "../../UI/DesignStandards";

const TeamsList = ({
  teams,
  onScoreUpdate,
  onTeamDelete,
  onVerificationChange,
  calculatingTotals,
  getCalculationResult,
  onInsertTeam,
  tournamentId,
  squadId,
  squads,
  events,
  canManage,
  canEnterScores,
  isPayoutFinalized,
  canVerifyTeamForEdit,
  verificationStatus,
  isScoreSaving,
  onReview,
  onRecapDelete,
  importedTeamIds = [],
}) => {
  // State to track which position is showing the create team form
  const [showCreateTeamAt, setShowCreateTeamAt] = useState(null);

  // Check if squads and events exist
  const hasSquads = squads && squads.length > 0;
  const hasEvents = events && events.length > 0;
  const canCreateTeam = hasSquads && hasEvents;

  if (!teams || teams.length === 0) {
    return (
      <div
        className="text-center py-12 text-brand-muted"
        role="status"
        aria-live="polite"
      >
        <div className="max-w-md mx-auto">
          <div className="text-6xl mb-4" aria-hidden="true">
            🏆
          </div>
          <h3 className="heading-display text-lg mb-2">
            No Teams Found
          </h3>
          <p className="text-sm text-brand-muted">
            No teams have been created for this tournament yet, or the current
            filters don't match any teams.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {teams.map((team, index) => (
        <React.Fragment key={team.id}>
          <TeamScoreCard
            team={team}
            onScoreUpdate={onScoreUpdate}
            onTeamDelete={onTeamDelete}
            onVerificationChange={onVerificationChange}
            calculatingTotals={calculatingTotals}
            getCalculationResult={getCalculationResult}
            canManage={canManage}
            canEnterScores={canEnterScores}
            tournamentId={tournamentId}
            isPayoutFinalized={isPayoutFinalized}
            canVerifyTeamForEdit={canVerifyTeamForEdit}
            verificationStatus={verificationStatus}
            isScoreSaving={isScoreSaving}
            onReview={onReview}
            onRecapDelete={onRecapDelete}
            importedTeamIds={importedTeamIds}
          />

          {/* Insert Team Button - Show between teams AND after the last team */}
          {canManage && (
            <div className="relative">
              {/* Subtle divider line */}
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-brand-charcoal/15"></div>
              </div>

              {/* Insert button - centered on the line */}
              <div className="relative flex justify-center">
                <button
                  onClick={() =>
                    setShowCreateTeamAt(
                      showCreateTeamAt === index + 1 ? null : index + 1
                    )
                  }
                  className={`inline-flex items-center justify-center bg-white min-h-11 min-w-11 px-3 rounded-lg border border-brand-charcoal/20 transition-all duration-200 z-10 ${
                    isPayoutFinalized || !canCreateTeam
                      ? "opacity-50 cursor-not-allowed border-brand-charcoal/10"
                      : "hover:border-brand-gold-ink hover:shadow-sm hover:bg-brand-cream"
                  }`}
                  title={
                    isPayoutFinalized
                      ? "Payouts have been locked and cannot be changed"
                      : !hasSquads
                      ? "Create squads before creating teams"
                      : !hasEvents
                      ? "Create events before creating teams"
                      : "Insert team here"
                  }
                  aria-label={`Insert new team after ${
                    team.teamName || "team"
                  } ${index + 1}`}
                  aria-expanded={showCreateTeamAt === index + 1}
                  aria-controls={`create-team-form-${index + 1}`}
                  disabled={isPayoutFinalized || !canCreateTeam}
                >
                  <FaPlus
                    className="w-4 h-4 text-brand-muted hover:text-brand-charcoal transition-colors duration-200"
                    aria-hidden="true"
                  />
                </button>
              </div>
            </div>
          )}

          {/* Show CreateTeam form inline if this position is selected */}
          {showCreateTeamAt === index + 1 &&
            !isPayoutFinalized &&
            canCreateTeam && (
              <div
                id={`create-team-form-${index + 1}`}
                className="card-board p-4 mt-4"
                role="region"
                aria-labelledby={`create-team-title-${index + 1}`}
              >
                <div className="flex justify-between items-center mb-4">
                  <h3
                    id={`create-team-title-${index + 1}`}
                    className="heading-display text-lg"
                  >
                    Create New Team
                  </h3>
                  <SecondaryButton
                    onClick={() => setShowCreateTeamAt(null)}
                    className="text-brand-muted hover:text-brand-charcoal"
                    aria-label="Cancel creating new team"
                  >
                    Cancel
                  </SecondaryButton>
                </div>
                <SinglePageTeamCreation
                  squadID={squadId || ""}
                  tournamentID={tournamentId || ""}
                  setTeams={(newTeams, shouldClose = true) => {
                    // Team was created successfully
                    if (shouldClose) {
                      setShowCreateTeamAt(null);
                    }
                    if (onInsertTeam) {
                      onInsertTeam(index + 1, newTeams);
                    }
                  }}
                  onClose={() => setShowCreateTeamAt(null)}
                  squads={squads}
                  teams={teams}
                />
              </div>
            )}
        </React.Fragment>
      ))}
    </div>
  );
};

export default TeamsList;
